import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Eye, MousePointerClick, Trash2, Power, PowerOff,
  ArrowUp, ArrowDown, Sparkles, ExternalLink, Loader2, Image as ImageIcon,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPromos = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/promos`);
      setPromos(res.data.promos || []);
    } catch (e) {
      toast.error('Erro ao carregar promos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const patch = async (id, body) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/promos/${id}`, body);
      await fetchAll();
    } catch (e) {
      toast.error(`Erro: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = (p) => patch(p.id, { active: !p.active });

  const reorder = async (index, dir) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= promos.length) return;
    const a = promos[index];
    const b = promos[newIndex];
    // Swap positions
    setBusyId(a.id);
    try {
      await Promise.all([
        axios.patch(`${API}/promos/${a.id}`, { position: b.position }),
        axios.patch(`${API}/promos/${b.id}`, { position: a.position }),
      ]);
      await fetchAll();
    } catch (e) {
      toast.error('Erro ao reordenar');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (p) => {
    const titlePreview = p.title?.pt || p.title?.en || 'esta promo';
    if (!window.confirm(`Eliminar "${titlePreview}"?`)) return;
    setBusyId(p.id);
    try {
      await axios.delete(`${API}/promos/${p.id}`);
      toast.success('Eliminada');
      await fetchAll();
    } catch (e) {
      toast.error(`Erro: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const activeCount = promos.filter((p) => p.active).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-10 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin/marketing"
            className="mb-2 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            data-testid="back-to-marketing"
          >
            <ArrowLeft className="h-4 w-4" /> Marketing AI
          </Link>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="flex items-center gap-3 text-3xl font-bold text-gray-900">
                <Sparkles className="h-8 w-8 text-amber-500" />
                Promos em Destaque
              </h1>
              <p className="mt-1 text-gray-600">
                Gere os destaques que aparecem no homepage · <strong>{activeCount} ativas</strong> de {promos.length}
              </p>
            </div>
            <Link
              to="/admin/marketing"
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-amber-700"
            >
              + Gerar Nova
            </Link>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
          </div>
        ) : promos.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
              <ImageIcon className="h-12 w-12 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-700">Nenhuma promo ainda</h3>
              <p className="max-w-sm text-sm text-gray-500">
                Vá ao gerador de Marketing AI, crie uma imagem e clique em "Publicar como Promo".
              </p>
              <Link
                to="/admin/marketing"
                className="mt-2 rounded-full bg-amber-600 px-5 py-2 text-sm font-medium text-white hover:bg-amber-700"
              >
                Ir para o Marketing AI
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {promos.map((p, index) => {
              const titlePT = p.title?.pt || p.title?.en || '(sem título)';
              const subtitle = p.subtitle?.pt || p.subtitle?.en || '';
              const langsCovered = ['pt', 'en', 'es', 'fr'].filter((l) => (p.title?.[l] || '').trim());
              return (
                <Card key={p.id} className={`overflow-hidden transition-all ${p.active ? 'border-amber-200' : 'border-gray-200 opacity-70'}`}>
                  <CardContent className="flex flex-col gap-4 p-4 md:flex-row">
                    <img
                      src={p.image_data_url}
                      alt={titlePT}
                      className="h-32 w-32 flex-shrink-0 rounded-lg object-cover ring-1 ring-gray-200"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${p.active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                          {p.active ? '● Ativa' : '○ Inativa'}
                        </span>
                        <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-700">
                          pos. {p.position}
                        </span>
                        <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">
                          {langsCovered.map((l) => l.toUpperCase()).join('/')}
                        </span>
                        {p.platform && (
                          <span className="rounded-full bg-purple-50 px-2.5 py-0.5 text-[11px] font-medium text-purple-700">
                            {p.platform}
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-bold text-gray-900 line-clamp-1" data-testid={`admin-promo-title-${p.id}`}>{titlePT}</h3>
                      {subtitle && <p className="text-sm text-gray-600 line-clamp-1">{subtitle}</p>}
                      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {p.views} views</span>
                        <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3.5 w-3.5" /> {p.clicks} cliques</span>
                        {p.cta_link && (
                          <span className="inline-flex items-center gap-1">
                            <ExternalLink className="h-3.5 w-3.5" /> <code className="text-[10px]">{p.cta_link}</code>
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex flex-row items-center gap-1 md:flex-col md:items-end">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={index === 0 || busyId === p.id}
                          onClick={() => reorder(index, -1)}
                          title="Subir"
                          data-testid={`move-up-${p.id}`}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={index === promos.length - 1 || busyId === p.id}
                          onClick={() => reorder(index, +1)}
                          title="Descer"
                          data-testid={`move-down-${p.id}`}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant={p.active ? 'outline' : 'default'}
                          disabled={busyId === p.id}
                          onClick={() => toggleActive(p)}
                          className={p.active ? '' : 'bg-green-600 text-white hover:bg-green-700'}
                          data-testid={`toggle-active-${p.id}`}
                        >
                          {p.active ? <><PowerOff className="mr-1 h-4 w-4" /> Desativar</> : <><Power className="mr-1 h-4 w-4" /> Ativar</>}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === p.id}
                          onClick={() => remove(p)}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`delete-promo-${p.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Footer help */}
        <p className="mt-8 text-center text-xs text-gray-400">
          As promos ativas aparecem na secção "Em Destaque" do homepage, ordenadas pela posição.
        </p>
      </div>
    </div>
  );
};

export default AdminPromos;
